'use client';

import { ChevronUpIcon } from '@app/app/_icons/ChevronUpIcon';
import { ReactNode, useCallback, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { motion } from 'motion/react';

interface Props {
    label: ReactNode;
    children: ReactNode;
    addon?: ReactNode;
    collapsable?: boolean;
    defaultCollapsed?: boolean;
    buttonClassName?: string;
    iconClassName?: string;
}

export function CollapsablePanel({
    label,
    children,
    addon,
    collapsable = true,
    defaultCollapsed = false,
    buttonClassName,
    iconClassName,
}: Props) {
    const [open, setOpen] = useState(!defaultCollapsed);

    const handleToggle = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();

            if (!collapsable) {
                return;
            }

            setOpen(isOpen => !isOpen);
        },
        [collapsable],
    );

    return (
        <div className="flex flex-col">
            {collapsable && (
                <button
                    className={twMerge(
                        'flex cursor-pointer items-center justify-between rounded bg-neutral-50 px-6 py-4 shadow-sm',
                        buttonClassName,
                    )}
                    onClick={handleToggle}>
                    <div className="text-lg font-medium">{label}</div>
                    {addon == null && (
                        <ChevronUpIcon
                            className={twMerge(
                                'size-5 text-primary-500 transition-transform',
                                !open && 'rotate-180',
                                iconClassName,
                            )}
                        />
                    )}
                    {addon != null && addon}
                </button>
            )}

            {!collapsable && (
                <div className="flex items-center justify-between rounded bg-neutral-50 px-6 py-4 shadow-sm">
                    <div className="text-lg font-medium">{label}</div>
                    {addon != null && addon}
                </div>
            )}

            <motion.div
                className={twJoin(!open && 'overflow-hidden')}
                initial={{
                    height: open ? 'auto' : 0,
                }}
                animate={{
                    height: open ? 'auto' : 0,
                }}>
                {children}
            </motion.div>
        </div>
    );
}
